import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ArrowLeft, School, User, Upload, X, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import Header from "../Header";
import AdminSidebar from "../Sidebar";
import { db, auth, storage } from "../../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ContentLoader from "../../authentication/ContentLoader";

// Define interfaces
interface Affiliation {
  role: string;
  schoolID?: string;
}

// Kenya's 47 counties
const kenyanCounties = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa", "Homa Bay", "Isiolo", "Kajiado",
  "Kakamega", "Kericho", "Kiambu", "Kilifi", "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia",
  "Lamu", "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa", "Murang'a", "Nairobi",
  "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua", "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River",
  "Tharaka-Nithi", "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot"
];

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  position: z.string().min(2, "Position must be at least 2 characters"),
  phoneNumber: z.string().regex(/^(?:\+254|0)7\d{8}$/, "Please enter a valid Kenyan phone number (e.g., +254712345678 or 0712345678)"),
  schoolName: z.string().min(2, "School name must be at least 2 characters"),
  schoolMotto: z.string().optional(),
  schoolEmail: z.string().email("Please enter a valid email address"),
  schoolAddress: z.string().min(5, "School address must be at least 5 characters"),
  county: z.string().min(1, "Please select a county"),
  schoolSystem: z.enum(["CBC", "8-4-4"], "Please select a school system"),
  schoolType: z.enum(["Boarding School", "Day School", "Mixed (Day & Boarding)"], "Please select a school type"),
});

type FormData = z.infer<typeof formSchema>;

interface SchoolRegistrationFormProps {
  onBack: () => void;
}

const SchoolRegistrationForm = ({ onBack }: SchoolRegistrationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSchool, setHasSchool] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      position: "",
      phoneNumber: "",
      schoolName: "",
      schoolMotto: "",
      schoolEmail: "",
      schoolAddress: "",
      county: "",
      schoolSystem: undefined,
      schoolType: undefined,
    },
  });

  // Load user data and set schoolID from Firebase auth claims
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUid(currentUser.uid);
        try {
          if (process.env.NODE_ENV !== "production") {
            console.log("Authenticating user with uid:", currentUser.uid);
          }
          // Refresh token to ensure claims are up-to-date
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log("ID Token Result claims:", idTokenResult.claims);
            console.log("Affiliations:", affiliations, "Default Affiliation:", defaultAffiliation, "Claim schoolID:", claimSchoolID);
          }

          // Select affiliation similar to App.tsx
          let selectedAffiliation: Affiliation | null = null;
          if (affiliations.length > 0) {
            if (defaultAffiliation && defaultAffiliation.role === "admin") {
              selectedAffiliation = defaultAffiliation;
            } else {
              selectedAffiliation = affiliations.find(aff => aff.role === "admin") || defaultAffiliation || affiliations[0];
            }
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID) {
            const selectedSchoolID = selectedAffiliation.schoolID;
            if (process.env.NODE_ENV !== "production") {
              console.log("Using schoolID from selectedAffiliation:", selectedSchoolID);
            }
            setSchoolID(selectedSchoolID);

            // Check if school details already exist
            try {
              const schoolDetailsDoc = await getDoc(doc(db, "schools", selectedSchoolID, "schoolDetails", "main"));
              if (schoolDetailsDoc.exists()) {
                const data = schoolDetailsDoc.data();
                if (data && data.name && data.name.trim() !== '') {
                  if (process.env.NODE_ENV !== "production") {
                    console.log("Existing school details found for schoolID:", selectedSchoolID);
                  }
                  setHasSchool(true);
                } else {
                  if (process.env.NODE_ENV !== "production") {
                    console.log("School details document exists but is incomplete (missing name) for schoolID:", selectedSchoolID);
                  }
                  setHasSchool(false);
                }
              } else {
                if (process.env.NODE_ENV !== "production") {
                  console.log("No existing school details for schoolID:", selectedSchoolID);
                }
                setHasSchool(false);
              }
            } catch (error: any) {
              if (error.code === "permission-denied") {
                if (process.env.NODE_ENV !== "production") {
                  console.log("Permission denied reading school details, possibly claims not fully set yet");
                }
                // Wait and retry once to allow claim propagation
                await new Promise(resolve => setTimeout(resolve, 5000));
                await currentUser.getIdToken(true);
                const retryIdTokenResult = await currentUser.getIdTokenResult(true);
                const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
                const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
                const retrySchoolID = retryDefaultAff?.schoolID || retryAffiliations.find(aff => aff.role === "admin")?.schoolID;

                if (retrySchoolID && retrySchoolID === selectedSchoolID) {
                  const retrySchoolDetailsDoc = await getDoc(doc(db, "schools", selectedSchoolID, "schoolDetails", "main"));
                  setHasSchool(retrySchoolDetailsDoc.exists());
                } else {
                  throw new Error("Failed to verify admin affiliation after retry");
                }
              } else {
                throw error;
              }
            }
          } else {
            if (process.env.NODE_ENV !== "production") {
              console.log("No valid selectedAffiliation for admin role, selectedAffiliation:", selectedAffiliation);
            }
            setSchoolID(null);
            toast({
              variant: "destructive",
              title: "Error",
              description: "No valid school ID found in user affiliation. Please contact support.",
            });
          }

          // Pre-fill form with user data
          const name = idTokenResult.claims.name as string | undefined;
          if (name) {
            const nameParts = name.split(" ");
            form.setValue("firstName", nameParts[0] || "");
            form.setValue("lastName", nameParts[1] || "");
          }

          setIsLoading(false);
        } catch (err: any) {
          console.error("Error loading user data for uid:", currentUser.uid, err);
          toast({
            variant: "destructive",
            title: "Error",
            description: `Failed to load user data: ${err.message || "Unknown error"}`,
          });
          setIsLoading(false);
        }
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.log("No user authenticated");
        }
        setUid(null);
        setSchoolID(null);
        setIsLoading(false);
        toast({
          variant: "destructive",
          title: "Error",
          description: "No user logged in. Please log in and try again.",
        });
      }
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log("Unsubscribing from onAuthStateChanged");
      }
      unsubscribe();
    };
  }, [form, toast]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: "Invalid file type",
          description: "Please upload an image file",
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "File too large",
          description: "Logo must be less than 5MB",
        });
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const onSubmit = async (data: FormData) => {
    if (!uid) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No user logged in. Please log in and try again.",
      });
      return;
    }

    if (hasSchool) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You have already registered a school.",
      });
      return;
    }

    if (!schoolID) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No school ID found in user affiliation. Please contact support.",
      });
      return;
    }

    if (process.env.NODE_ENV !== "production") {
      console.log("Submitting registration with schoolID:", schoolID);
    }
    setIsSubmitting(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User is not authenticated");
      }

      // Ensure claims are up-to-date
      await currentUser.getIdToken(true);
      const idTokenResult = await currentUser.getIdTokenResult(true);
      const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
      const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
      const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

      if (process.env.NODE_ENV !== "production") {
        console.log("Claims before write:", { affiliations, defaultAffiliation, claimSchoolID });
      }

      // Verify admin role and schoolID
      const adminAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);
      if (
        !adminAffiliation ||
        (defaultAffiliation?.role !== "admin" || defaultAffiliation?.schoolID !== schoolID) ||
        claimSchoolID !== schoolID
      ) {
        // Wait and retry once to handle propagation delay
        await new Promise(resolve => setTimeout(resolve, 5000));
        await currentUser.getIdToken(true);
        const retryIdTokenResult = await currentUser.getIdTokenResult(true);
        const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const retryClaimSchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

        if (process.env.NODE_ENV !== "production") {
          console.log("Retry claims:", { retryAffiliations, retryDefaultAff, retryClaimSchoolID });
        }

        const retryAdminAffiliation = retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);
        if (
          !retryAdminAffiliation ||
          (retryDefaultAff?.role !== "admin" || retryDefaultAff?.schoolID !== schoolID) ||
          retryClaimSchoolID !== schoolID
        ) {
          throw new Error(
            `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, ` +
            `defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retryClaimSchoolID}`
          );
        }
      }

      // Write to Firestore
      try {
        // Upload logo if provided
        let logoUrl = "";
        if (logoFile) {
          const logoRef = ref(storage, `schools/${schoolID}/logos/logo_${logoFile.name}`);
          await uploadBytes(logoRef, logoFile);
          logoUrl = await getDownloadURL(logoRef);
        }

        // Write to users/{uid}/schoolteaching/{schoolID}
        await setDoc(doc(db, "users", uid, "schoolteaching", schoolID), {
          position: data.position,
          phoneNumber: data.phoneNumber,
          createdAt: new Date(),
        });

        // Generate Account Number
        const cleanName = data.schoolName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        const uniqueSuffix = schoolID.slice(-4).toUpperCase();
        const accountNumber = `${cleanName.substring(0, 8)}${uniqueSuffix}`;

        // Create school details document
        await setDoc(doc(db, "schools", schoolID, "schoolDetails", "main"), {
          name: data.schoolName,
          motto: data.schoolMotto,
          email: data.schoolEmail,
          address: data.schoolAddress,
          system: data.schoolSystem,
          type: data.schoolType,
          county: data.county,
          logoUrl: logoUrl || "",
          accountNumber: accountNumber,
          createdAt: new Date(),
          createdBy: uid,
        });

        // Update users/{uid}
        await setDoc(doc(db, "users", uid), {
          firstName: data.firstName,
          lastName: data.lastName,
          updatedAt: new Date(),
        }, { merge: true });

        toast({
          title: "Success",
          description: "School registered successfully! Setting up your subscription...",
        });
        form.reset();
        setLogoFile(null);
        setLogoPreview(null);
        // NOTE: Navigation to /dashboard/subscription is handled by the real-time
        // listener in App.tsx which detects when school name is written to Firestore.
      } catch (error: any) {
        if (error.code === "permission-denied") {
          console.error("Permission denied during Firestore writes:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Permission denied. Please ensure your account has admin privileges and try again.",
          });
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error("Error registering school with schoolID:", schoolID, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to register school: ${error.message || "Unknown error"}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 overflow-y-auto p-3 sm:p-6">
            <ContentLoader isLoading={isLoading} message="">
            <div className="max-w-2xl mx-auto">
              {hasSchool ? (
                <div className="flex items-center justify-center min-h-[60vh]">
                  <Card className="w-full max-w-md border-l-4 border-l-green-500 shadow-lg bg-white dark:bg-gray-800">
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto h-16 w-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                      <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">School Registered</CardTitle>
                      <CardDescription className="text-base mt-2">
                        Your account is already associated with a registered institution.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6 pt-4">
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-100 dark:border-gray-700 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Registered School ID</p>
                        <p className="text-xl font-mono font-semibold text-blue-600 dark:text-blue-400 tracking-wider">
                          {schoolID || "N/A"}
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                          You can manage your school profile, update details, and configure settings from the dashboard.
                        </p>
                        <Button 
                          onClick={onBack} 
                          className="w-full gap-2 bg-gray-900 hover:bg-gray-800 text-white dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 transition-all duration-200 shadow-sm" 
                          size="lg"
                        >
                          <ArrowLeft className="h-4 w-4" />
                          Return to Settings
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (process.env.NODE_ENV !== "production") {
                          console.log("Back to Settings clicked");
                        }
                        onBack();
                      }}
                      className="mb-4 hover:bg-gray-100"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Settings
                    </Button>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <School className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">School Registration</h1>
                        <p className="text-gray-600">Register your school with the system</p>
                      </div>
                    </div>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>School Information</CardTitle>
                      <CardDescription>
                        Please provide the following details to register your school
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-3">
                              <User className="h-4 w-4 text-blue-600" />
                              <h3 className="font-semibold text-gray-800">Personal Information</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>First Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Enter your first name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Last Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Enter your last name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <FormField
                              control={form.control}
                              name="position"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Capacity / Position in School</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Headteacher, Principal, Administrator" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="phoneNumber"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Phone Number</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., +254712345678 or 0712345678" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-3">
                              <School className="h-4 w-4 text-blue-600" />
                              <h3 className="font-semibold text-gray-800">School Information</h3>
                            </div>
                                                          <FormField
                                                            control={form.control}
                                                            name="schoolName"
                                                            render={({ field }) => (
                                                              <FormItem>
                                                                <FormLabel>School Name</FormLabel>
                                                                <FormControl>
                                                                  <Input placeholder="Enter your school name" {...field} />
                                                                </FormControl>
                                                                <FormMessage />
                                                              </FormItem>
                                                            )}
                                                          />
                                                          <FormField
                                                            control={form.control}
                                                            name="schoolMotto"
                                                            render={({ field }) => (
                                                              <FormItem>
                                                                <FormLabel>School Motto (Optional)</FormLabel>
                                                                <FormControl>
                                                                  <Input placeholder="Enter your school motto" {...field} />
                                                                </FormControl>
                                                                <FormMessage />
                                                              </FormItem>
                                                            )}
                                                          />
                                                        
                                                        {/* Logo Upload */}                            <div className="space-y-2">
                              <FormLabel>School Logo (Optional)</FormLabel>
                              <div className="flex items-center gap-4">
                                {logoPreview ? (
                                  <div className="relative">
                                    <img
                                      src={logoPreview}
                                      alt="School logo preview"
                                      className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                                    />
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                                      onClick={removeLogo}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors bg-transparent">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                                      <p className="text-sm text-gray-500">Click to upload logo</p>
                                      <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                                    </div>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/*"
                                      onChange={handleLogoChange}
                                    />
                                  </label>
                                )}
                              </div>
                            </div>

                            <FormField
                              control={form.control}
                              name="schoolEmail"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>School Email</FormLabel>
                                  <FormControl>
                                    <Input type="email" placeholder="school@example.com" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="schoolAddress"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>School Address</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Enter complete school address" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="county"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>County</FormLabel>
                                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                          <SelectTrigger className="bg-gray-50/50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:bg-white dark:focus:bg-gray-950 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200">
                                            <SelectValue placeholder="Select county" />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent className="bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-xl">
                                        {kenyanCounties.map((county) => (
                                          <SelectItem 
                                            key={county} 
                                            value={county}
                                            className="focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:text-blue-600 dark:focus:text-blue-400 cursor-pointer transition-colors"
                                          >
                                            {county}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="schoolSystem"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>School System</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                      <FormControl>
                                        <SelectTrigger className="bg-gray-50/50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:bg-white dark:focus:bg-gray-950 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200">
                                          <SelectValue placeholder="Select school system" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent className="bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-xl">
                                        <SelectItem value="CBC" className="focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:text-blue-600 dark:focus:text-blue-400 cursor-pointer transition-colors">CBC (Competency Based Curriculum)</SelectItem>
                                        <SelectItem value="8-4-4" className="focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:text-blue-600 dark:focus:text-blue-400 cursor-pointer transition-colors">8-4-4 System</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <FormField
                              control={form.control}
                              name="schoolType"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>School Type</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger className="bg-gray-50/50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:bg-white dark:focus:bg-gray-950 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200">
                                        <SelectValue placeholder="Select school type" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent className="bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-800 shadow-xl">
                                      <SelectItem value="Boarding School" className="focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:text-blue-600 dark:focus:text-blue-400 cursor-pointer transition-colors">Boarding School</SelectItem>
                                      <SelectItem value="Day School" className="focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:text-blue-600 dark:focus:text-blue-400 cursor-pointer transition-colors">Day School</SelectItem>
                                      <SelectItem value="Mixed (Day & Boarding)" className="focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:text-blue-600 dark:focus:text-blue-400 cursor-pointer transition-colors">Mixed (Day & Boarding)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="flex gap-3 pt-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                if (process.env.NODE_ENV !== "production") {
                                  console.log("Cancel clicked");
                                }
                                onBack();
                              }}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={isSubmitting || !schoolID}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                              {isSubmitting ? "Registering..." : "Register School"}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
            </ContentLoader>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SchoolRegistrationForm;